import {
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { PrettoSlider } from "../../../assets/styles/styles";
import { CompanySpacesService } from "../../../service/CompanySpacesService";
import { useAuthStore } from "../../../store/storeAuth";
import useFlowsStore from "../../../store/storeFlows";
import {
  AiTextGeneratorFormValues,
  OpenAIModels,
  anthropicAiModels,
  getDeprecatedModelMigration,
  systemDisplayNameMap,
  systemValueMap,
} from "../../AiConfigs/constants";
import KitModal from "../../UI/KitModal";
import Gemini from "../Gemini";
import GroqAi from "../GroqAi";
import OpenAi from "../OpenAi";
import OpenSources from "../OpenSources";
import XAi from "../XAi";
import { UserService } from "../../../service/UserService";
import { CompanyService } from "../../../service/CompanyService";
type Props = {
  onHide: () => void;
  show: boolean;
  formData: AiTextGeneratorFormValues;
  onChange: (e: any) => void;
  nodeData?: any;
};

const SettingsModal = ({
  onHide,
  show,
  formData,
  onChange,
  nodeData,
}: Props) => {
  const {
    user,
    technologies,
    companySpaceTechnologies,
    setCompanySpaceTechnologies,
    setTechnologies,
  } = useAuthStore((state) => state);
  const { spaceId } = useFlowsStore();
  const [anthropicAutoSettings, setAnthropicAutoSettings] = useState(
    formData.autoToken,
  );
  const [perplexityAutoSettings, setPerplexityAutoSettings] = useState(
    formData.autoToken,
  );

  useEffect(() => {
    if (!show) {
      return;
    }

    const fetchAllTechnologies = async () => {
      try {
        if (spaceId) {
          const res = await CompanySpacesService.getSpaceTechnologies(
            spaceId as string,
          );
          setCompanySpaceTechnologies(res.data);
        }

        if (technologies.length === 0) {
          const technologiesData = user?.selectedCompanyId
            ? await CompanyService.getCompanyTechnologies(
                user.selectedCompanyId,
              )
            : await UserService.getTechnologies();
          setTechnologies(technologiesData.data);
        }
      } catch (error) {
        console.log("Error fetching technologies:", error);
      }
    };

    fetchAllTechnologies();
  }, [spaceId, show, user?.selectedCompanyId]);

  const textSystems = [
    "anthropic",
    "openAi",
    "groq",
    "gemini",
    "perplexity",
    "replicate",
    "xai",
  ];

  const filteredTextSystems = (() => {
    const systemsToUse =
      spaceId && companySpaceTechnologies.length > 0
        ? companySpaceTechnologies
        : technologies;

    return (
      systemsToUse
        ?.filter((system) => textSystems?.includes(system?.system))
        ?.map((system) => ({
          ...system,
          models: system?.models?.filter(
            (model: any) =>
              model.type === "Text" && !model.isDeprecated,
          ),
        })) || []
    );
  })();

  useEffect(() => {
    if (!show || filteredTextSystems.length === 0) return;

    const systemsToUse =
      spaceId && companySpaceTechnologies.length > 0
        ? companySpaceTechnologies
        : technologies;

    const migration = getDeprecatedModelMigration(
      formData.AiSystem,
      formData,
      systemsToUse,
    );

    if (migration) {
      onChange({
        target: { name: migration.fieldName, value: migration.newModel },
      });
    }
  }, [show, technologies, companySpaceTechnologies]);

  const isWebSearchAvailable = (): boolean => {
    if (formData.AiSystem === "OpenAI") {
      if (
        formData.aiModel === OpenAIModels.O3_MINI_2025_01_31 ||
        formData.aiModel === OpenAIModels.O1 ||
        formData.aiModel === OpenAIModels.GPT5 ||
        formData.aiModel === OpenAIModels.GPT5_MINI ||
        formData.aiModel === OpenAIModels.GPT5_NANO ||
        formData.aiModel === OpenAIModels.GPT5_1 ||
        formData.aiModel === OpenAIModels.GPT5_2
      ) {
        return true;
      }
    }

    if (formData.AiSystem === "anthropicAi") {
      if (anthropicAiModels.includes(formData.anthropicModel)) {
        return true;
      }
    }

    if (formData.AiSystem === "Gemini") {
      if (
        formData.geminiModel === "gemini-2.5-pro" ||
        formData.geminiModel === "gemini-2.5-flash" ||
        formData.geminiModel === "gemini-2.5-flash-lite" ||
        formData.geminiModel === "gemini-2.0-flash"
      ) {
        return true;
      }
    }

    return false;
  };

  return (
    <KitModal
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          maxWidth: "650px",
          borderRadius: "16px",
        },
        "& .MuiDialogContent-root": {
          padding: "16px",
        },
        "& .MuiDialogActions-root": {
          padding: "16px",
        },
      }}
      onHide={onHide}
      show={show}
    >
      <Box>
        <Typography
          variant="h6"
          color={user?.theme === "dark" ? "#D0D5DD" : "black"}
        >
          Advanced Settings
        </Typography>
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: "16px" }}
          mt={"16px"}
          p={"8px"}
          borderRadius={"4px"}
        >
          <Typography
            fontSize={"14px"}
            variant="h6"
            color={user?.theme === "dark" ? "#D0D5DD" : "black"}
          >
            Prompt Input
          </Typography>

          <FormControl sx={{ width: "100%" }}>
            <InputLabel id="demo-simple-select-label">Ai System</InputLabel>
            <Select
              name="AiSystem"
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              label="Ai System"
              value={formData.AiSystem}
              onChange={onChange}
              sx={{
                width: "100%",
                "& .MuiList-root": {
                  backgroundColor:
                    user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                },
                bgcolor: user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                border: `2px solid ${
                  user?.theme === "dark" ? "#475467" : "#EAECF0"
                }`,
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    "& .Mui-selected": {
                      backgroundColor:
                        user?.theme === "dark" ? "#667085" : "#D0D5DD",
                    },
                    backgroundColor:
                      user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                  },
                },
              }}
            >
              {filteredTextSystems?.map((opt, index) => {
                const disabled = opt?.models?.every(
                  (model: any) => model.enabled === false,
                );
                const disabledByCompany = opt?.models?.every(
                  (model: any) => model.disabledByCompany,
                );
                const systemValue = systemValueMap[opt.system] || opt.system;
                const displayName =
                  systemDisplayNameMap[systemValue] || systemValue;
                return (
                  <MenuItem key={index} value={systemValue} disabled={disabled}>
                    <Box
                      sx={{
                        display: "flex",
                        width: "100%",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography>{displayName}</Typography>
                      {disabledByCompany && (
                        <Chip
                          label="Disabled by admin"
                          color="error"
                          size="small"
                          variant="filled"
                          sx={{
                            borderRadius: "8px",
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {formData.AiSystem === "OpenAI" && (
            <OpenAi
              systems={filteredTextSystems}
              onChange={onChange}
              watch={formData}
              tokenCount={0}
            />
          )}

          {formData.AiSystem === "Groq" && (
            <GroqAi
              onChange={onChange}
              watch={formData}
              systems={filteredTextSystems}
            />
          )}

          {formData.AiSystem === "Google" && (
            <>
              <FormLabel
                sx={{
                  mb: "4px",
                  color: user?.theme === "dark" ? "#D0D5DD" : "black",
                }}
                htmlFor="googleType"
              >
                Google System
              </FormLabel>
              <br />
              <Select
                className="nodrag"
                value={formData.googleType}
                size="small"
                onChange={onChange}
                name={"googleType"}
                sx={{
                  "& .MuiList-root": {
                    backgroundColor:
                      user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                  },
                  bgcolor: user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                  color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                  border: `2px solid ${
                    user?.theme === "dark" ? "#475467" : "#EAECF0"
                  }`,
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      "& .Mui-selected": {
                        backgroundColor:
                          user?.theme === "dark" ? "#667085" : "#D0D5DD",
                      },
                      backgroundColor:
                        user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                    },
                  },
                }}
              >
                <MenuItem
                  key={"Gemini"}
                  value={"Gemini"}
                  sx={{
                    bgcolor: user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                    color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                  }}
                >
                  {"Gemini"}
                </MenuItem>
              </Select>
            </>
          )}

          {formData.AiSystem === "Gemini" && (
            <Gemini
              imageSystem
              watch={formData}
              onChange={onChange}
              systems={filteredTextSystems}
            />
          )}

          {formData.AiSystem === "openSources" && (
            <OpenSources
              watch={formData}
              onChange={onChange}
              systems={filteredTextSystems}
            />
          )}

          {formData.AiSystem === "perplexityAi" && (
            <>
              <Box>
                <FormLabel
                  sx={{
                    mb: "8px",
                    mt: "8px",
                    color: user?.theme === "dark" ? "#D0D5DD" : "black",
                  }}
                >
                  Type
                </FormLabel>
                <Select
                  className="nodrag"
                  value={formData.perplexityType}
                  fullWidth
                  size="small"
                  onChange={onChange}
                  name={"perplexityType"}
                  sx={{
                    "& .MuiList-root": {
                      backgroundColor:
                        user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                    },
                    bgcolor: user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                    color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                    border: `2px solid ${
                      user?.theme === "dark" ? "#475467" : "#EAECF0"
                    }`,
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        "& .Mui-selected": {
                          backgroundColor:
                            user?.theme === "dark" ? "#667085" : "#D0D5DD",
                        },
                        backgroundColor:
                          user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                      },
                    },
                  }}
                >
                  {filteredTextSystems
                    ?.filter((item: any) => item.system === "perplexity")?.[0]
                    ?.models?.map((opt: any) => (
                      <MenuItem
                        key={opt.id}
                        value={opt.model}
                        disabled={opt.disabledByCompany}
                      >
                        <Box display={"flex"} alignItems={"center"} gap={"8px"}>
                          <Typography
                            variant="body2"
                            color={user?.theme === "dark" ? "#D0D5DD" : "black"}
                          >
                            {opt.model}
                          </Typography>
                          {opt.disabledByCompany && (
                            <Typography fontSize={"12px"} color="red">
                              (disabled by admin)
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                </Select>
              </Box>

              <Box>
                <FormLabel
                  sx={{
                    mb: "8px",
                    mt: "8px",
                    color: user?.theme === "dark" ? "#D0D5DD" : "black",
                  }}
                >
                  Search Context Size
                </FormLabel>

                <Select
                  className="nodrag"
                  value={formData.search_context_size}
                  fullWidth
                  size="small"
                  onChange={onChange}
                  name={"search_context_size"}
                  sx={{
                    "& .MuiList-root": {
                      backgroundColor:
                        user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                    },
                    bgcolor: user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                    color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                    border: `2px solid ${
                      user?.theme === "dark" ? "#475467" : "#EAECF0"
                    }`,
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        "& .Mui-selected": {
                          backgroundColor:
                            user?.theme === "dark" ? "#667085" : "#D0D5DD",
                        },
                        backgroundColor:
                          user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                      },
                    },
                  }}
                >
                  <MenuItem value="low">
                    <Typography>Low</Typography>
                  </MenuItem>
                  <MenuItem value="medium">
                    <Typography>Medium</Typography>
                  </MenuItem>
                  <MenuItem value="high">
                    <Typography>High</Typography>
                  </MenuItem>
                </Select>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: "20px",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      onChange={(e) => {
                        onChange({
                          target: {
                            name: "autoToken",
                            value: !formData.autoToken,
                          },
                        });

                        setPerplexityAutoSettings(!formData.autoToken);
                      }}
                      name="autoToken"
                      checked={formData.autoToken}
                      sx={{
                        margin: 0,
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    />
                  }
                  label="Auto setting"
                />
                <FormControl>
                  <TextField
                    id="demo-simple-select"
                    type={"number"}
                    onChange={onChange}
                    value={
                      perplexityAutoSettings ? 0 : formData.perplexityTokens
                    }
                    disabled={perplexityAutoSettings}
                    className="nodrag"
                    label={"Output Token Max"}
                    name={"max_tokens"}
                    InputProps={{
                      style: {
                        color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                      },
                    }}
                    sx={{
                      width: "290px",
                      color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                      bgcolor: user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                      border: `2px solid ${
                        user?.theme === "dark" ? "#475467" : "#EAECF0"
                      }`,
                    }}
                  />
                </FormControl>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <FormLabel
                  sx={{
                    bgcolor: user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                    color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                  }}
                >
                  Top P
                </FormLabel>
                <PrettoSlider
                  step={0.1}
                  min={0}
                  name={"perplexityTopP"}
                  value={Number(formData.perplexityTopP)}
                  max={1}
                  style={{ marginLeft: "8px", width: "350px" }}
                  onChange={(e) => {
                    onChange(e);
                  }}
                  className="nodrag"
                />
                <TextField
                  type={"number"}
                  value={
                    formData[
                      "perplexityTopP" as keyof AiTextGeneratorFormValues
                    ]
                  }
                  name={"perplexityTopP"}
                  onChange={(e) => {
                    onChange(e);
                  }}
                  InputProps={{
                    inputProps: {
                      min: 1,
                      max: 40,
                      step: 1,
                    },
                    style: {
                      color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                    },
                  }}
                  style={{ marginLeft: "8px", width: "80px" }}
                  className="nodrag"
                  sx={{
                    color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                    bgcolor: user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                    border: `2px solid ${
                      user?.theme === "dark" ? "#475467" : "#EAECF0"
                    }`,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <FormLabel
                  sx={{
                    bgcolor: user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                    color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                  }}
                >
                  Temperature (Creativity of LLM response)
                </FormLabel>
                <PrettoSlider
                  step={0.1}
                  min={0}
                  name={"perplexityTemperature"}
                  value={Number(formData.perplexityTemperature)}
                  max={2}
                  style={{ marginLeft: "8px", width: "350px" }}
                  onChange={(e) => {
                    onChange(e);
                  }}
                  className="nodrag"
                />
                <TextField
                  type={"number"}
                  value={
                    formData[
                      "perplexityTemperature" as keyof AiTextGeneratorFormValues
                    ]
                  }
                  name={"perplexityTemperature"}
                  onChange={(e) => {
                    onChange(e);
                  }}
                  InputProps={{
                    inputProps: {
                      min: 0,
                      max: 1,
                      step: 0.1,
                    },
                    style: {
                      color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                    },
                  }}
                  style={{ marginLeft: "8px", width: "80px" }}
                  className="nodrag"
                  sx={{
                    color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                    bgcolor: user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                    border: `2px solid ${
                      user?.theme === "dark" ? "#475467" : "#EAECF0"
                    }`,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <FormLabel
                  sx={{
                    bgcolor: user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                    color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                  }}
                >
                  Top K
                </FormLabel>
                <PrettoSlider
                  step={1}
                  min={0}
                  name={"perplexityTopK"}
                  value={Number(formData.perplexityTopK)}
                  max={40}
                  style={{ marginLeft: "8px", width: "350px" }}
                  onChange={(e) => {
                    onChange(e);
                  }}
                  className="nodrag"
                />
                <TextField
                  type={"number"}
                  value={
                    formData[
                      "perplexityTopK" as keyof AiTextGeneratorFormValues
                    ]
                  }
                  name={"perplexityTopK"}
                  onChange={(e) => {
                    onChange(e);
                  }}
                  InputProps={{
                    inputProps: {
                      min: 1,
                      max: 40,
                      step: 1,
                    },
                    style: {
                      color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                    },
                  }}
                  style={{ marginLeft: "8px", width: "80px" }}
                  className="nodrag"
                  sx={{
                    color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                    bgcolor: user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                    border: `2px solid ${
                      user?.theme === "dark" ? "#475467" : "#EAECF0"
                    }`,
                  }}
                />
              </Box>
            </>
          )}

          {formData.AiSystem === "anthropicAi" && (
            <>
              <Box mt={"4px"}>
                <Typography
                  fontSize={"14px"}
                  fontWeight={500}
                  sx={{
                    mb: "4px",
                    color: user?.theme === "dark" ? "#D0D5DD" : "black",
                  }}
                  variant="body1"
                >
                  Model
                </Typography>
                <FormControl sx={{ width: "100%" }}>
                  <Select
                    className="nodrag"
                    name="anthropicModel"
                    fullWidth
                    size="small"
                    value={formData.anthropicModel}
                    sx={{
                      height: "unset",
                      bgcolor: user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                      color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                    }}
                    onChange={onChange}
                    MenuProps={{
                      style: {
                        maxHeight: "700px",
                      },
                    }}
                  >
                    {(filteredTextSystems
                      ?.find((item: any) => item?.system === "anthropic")
                      ?.models?.filter((m: any) => m.type === "Text") || []
                    ).map((opt: any, index: number) => (
                        <MenuItem
                          className="nodrag"
                          key={opt.model + index}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "start",
                            alignItems: "start",
                            padding: "16px",
                          }}
                          value={opt.model}
                          disabled={opt.enabled === false || opt.disabledByCompany}
                        >
                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            gap={"8px"}
                          >
                            <Typography
                              mb={"4px"}
                              textTransform={"capitalize"}
                              variant="body2"
                              color={
                                user?.theme === "dark" ? "#D0D5DD" : "black"
                              }
                            >
                              {opt.displayName || opt.model}
                            </Typography>
                            {opt.disabledByCompany && (
                              <Typography fontSize={"12px"} color="red">
                                (disabled by admin)
                              </Typography>
                            )}
                          </Box>
                          <Box
                            display={"flex"}
                            gap={"8px"}
                            alignItems={"center"}
                            flexWrap={"wrap"}
                          >
                            {opt?.tags?.map((tag: any, index: number) => {
                              const tagLabel =
                                typeof tag === "string" ? tag : tag.label;
                              const tagTooltip =
                                typeof tag === "string" ? "" : tag.tooltip;

                              return (
                                <Tooltip
                                  key={tagLabel + index}
                                  title={tagTooltip}
                                  arrow
                                  placement="top"
                                >
                                  <Chip
                                    sx={{
                                      borderRadius: "4px",
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      border: `1px solid ${
                                        user?.theme === "dark"
                                          ? "#475467"
                                          : "#EAECF0"
                                      }`,
                                      color:
                                        user?.theme === "dark"
                                          ? "#D0D5DD"
                                          : "black",
                                    }}
                                    label={tagLabel}
                                  />
                                </Tooltip>
                              );
                            })}
                          </Box>
                          <Typography
                            maxWidth={"570px"}
                            variant="body2"
                            sx={{
                              color: "#667085",
                              mt: "8px",
                              whiteSpace: "pre-wrap",
                              textAlign: "start",
                            }}
                          >
                            {opt.description}
                          </Typography>
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: "20px",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      onChange={(e) => {
                        onChange({
                          target: {
                            name: "autoToken",
                            value: !formData.autoToken,
                          },
                        });

                        setAnthropicAutoSettings(!formData.autoToken);
                      }}
                      name="autoToken"
                      checked={formData.autoToken}
                      sx={{
                        margin: 0,
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    />
                  }
                  label="Auto setting"
                />
                <FormControl>
                  <TextField
                    id="demo-simple-select"
                    type={"number"}
                    onChange={onChange}
                    value={anthropicAutoSettings ? 0 : formData.anthropicToken}
                    disabled={anthropicAutoSettings}
                    className="nodrag"
                    label={"Output Token Max"}
                    name={"anthropicToken"}
                    InputProps={{
                      style: {
                        color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                      },
                    }}
                    sx={{
                      width: "290px",
                      color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                      bgcolor: user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                      border: `2px solid ${
                        user?.theme === "dark" ? "#475467" : "#EAECF0"
                      }`,
                    }}
                  />
                </FormControl>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <FormLabel
                  sx={{
                    bgcolor: user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                    color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                  }}
                >
                  Top K
                </FormLabel>
                <PrettoSlider
                  step={1}
                  min={1}
                  name={"anthropicTopK"}
                  value={Number(formData.anthropicTopK)}
                  max={40}
                  style={{ marginLeft: "8px", width: "350px" }}
                  onChange={(e) => {
                    onChange(e);
                  }}
                  className="nodrag"
                />
                <TextField
                  type={"number"}
                  value={
                    formData["anthropicTopK" as keyof AiTextGeneratorFormValues]
                  }
                  name={"anthropicTopK"}
                  onChange={(e) => {
                    onChange(e);
                  }}
                  InputProps={{
                    inputProps: {
                      min: 1,
                      max: 40,
                      step: 1,
                    },
                    style: {
                      color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                    },
                  }}
                  style={{ marginLeft: "8px", width: "80px" }}
                  className="nodrag"
                  sx={{
                    color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                    bgcolor: user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                    border: `2px solid ${
                      user?.theme === "dark" ? "#475467" : "#EAECF0"
                    }`,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <FormLabel
                  sx={{
                    bgcolor: user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                    color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                  }}
                >
                  Top P
                </FormLabel>
                <PrettoSlider
                  step={1}
                  min={1}
                  name={"anthropicTopP"}
                  value={Number(formData.anthropicTopP)}
                  max={40}
                  style={{ marginLeft: "8px", width: "350px" }}
                  onChange={(e) => {
                    onChange(e);
                  }}
                  className="nodrag"
                />
                <TextField
                  type={"number"}
                  value={
                    formData["anthropicTopP" as keyof AiTextGeneratorFormValues]
                  }
                  name={"anthropicTopP"}
                  onChange={(e) => {
                    onChange(e);
                  }}
                  InputProps={{
                    inputProps: {
                      min: 1,
                      max: 40,
                      step: 1,
                    },
                    style: {
                      color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                    },
                  }}
                  style={{ marginLeft: "8px", width: "80px" }}
                  className="nodrag"
                  sx={{
                    color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                    bgcolor: user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                    border: `2px solid ${
                      user?.theme === "dark" ? "#475467" : "#EAECF0"
                    }`,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <FormLabel
                  sx={{
                    bgcolor: user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                    color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                  }}
                >
                  Temperature (Creativity of LLM response)
                </FormLabel>
                <PrettoSlider
                  step={0.1}
                  min={0}
                  name={"anthropicTemperature"}
                  value={Number(formData.anthropicTemperature)}
                  max={1}
                  style={{ marginLeft: "8px", width: "350px" }}
                  onChange={(e) => {
                    onChange(e);
                  }}
                  className="nodrag"
                />
                <TextField
                  type={"number"}
                  value={
                    formData[
                      "anthropicTemperature" as keyof AiTextGeneratorFormValues
                    ]
                  }
                  name={"anthropicTemperature"}
                  onChange={(e) => {
                    onChange(e);
                  }}
                  InputProps={{
                    inputProps: {
                      min: 0,
                      max: 1,
                      step: 0.1,
                    },
                    style: {
                      color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                    },
                  }}
                  style={{ marginLeft: "8px", width: "80px" }}
                  className="nodrag"
                  sx={{
                    color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                    bgcolor: user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                    border: `2px solid ${
                      user?.theme === "dark" ? "#475467" : "#EAECF0"
                    }`,
                  }}
                />
              </Box>
            </>
          )}

          {formData.AiSystem === "XAi" && (
            <XAi
              systems={filteredTextSystems}
              onChange={onChange}
              watch={formData}
              tokenCount={0}
            />
          )}
          <Box mt={"16px"}>
            <Typography
              fontSize={"14px"}
              variant="h6"
              color={user?.theme === "dark" ? "#D0D5DD" : "black"}
            >
              Multimodal Settings
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                marginTop: "8px",
              }}
            >
              <FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={
                        nodeData?.isEmptyState
                          ? true
                          : formData.multimodalEnabled
                            ? true
                            : false
                      }
                      onChange={(e) => {
                        onChange({
                          target: {
                            name: "multimodalEnabled",
                            value: e.target.checked,
                          },
                        });
                      }}
                      name="multimodalEnabled"
                    />
                  }
                  label="Enable Multimodal AI Models"
                />
              </FormControl>
            </Box>
          </Box>

          {isWebSearchAvailable() && (
            <Box mt={"16px"}>
              <Typography
                fontSize={"14px"}
                variant="h6"
                color={user?.theme === "dark" ? "#D0D5DD" : "black"}
              >
                Web Search Settings
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  marginTop: "8px",
                }}
              >
                <FormControl>
                  <FormControlLabel
                    control={
                      <Switch
                        onChange={(e) => {
                          onChange({
                            target: {
                              name: "webSearch",
                              value: !formData.webSearch,
                            },
                          });
                        }}
                        name="webSearch"
                        checked={formData.webSearch || false}
                        sx={{
                          margin: 0,
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      />
                    }
                    label="Web search"
                  />
                </FormControl>
              </Box>
            </Box>
          )}

          <Box mt={"16px"}>
            <Typography
              fontSize={"14px"}
              variant="h6"
              color={user?.theme === "dark" ? "#D0D5DD" : "black"}
            >
              Streaming Settings
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                marginTop: "8px",
              }}
            >
              <FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.streamEnabled}
                      onChange={(e) => {
                        onChange({
                          target: {
                            name: "streamEnabled",
                            value: e.target.checked,
                          },
                        });
                      }}
                      name="streamEnabled"
                    />
                  }
                  label="Stream Enabled"
                />
              </FormControl>

              <FormControl
                disabled={!formData.streamEnabled}
                sx={{ width: "100%" }}
              >
                <InputLabel id="stream-type-label">Stream Type</InputLabel>

                <Select
                  name="streamType"
                  labelId="stream-type-label"
                  id="stream-type-select"
                  label="Stream Type"
                  value={formData.streamType}
                  onChange={onChange}
                  sx={{
                    width: "100%",
                    "& .MuiList-root": {
                      backgroundColor:
                        user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                    },
                    bgcolor: user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                    color: user?.theme === "dark" ? "#D0D5DD" : "#101828",
                    border: `2px solid ${
                      user?.theme === "dark" ? "#475467" : "#EAECF0"
                    }`,
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        "& .Mui-selected": {
                          backgroundColor:
                            user?.theme === "dark" ? "#667085" : "#D0D5DD",
                        },
                        backgroundColor:
                          user?.theme === "dark" ? "#1D2939" : "#F9FAFB",
                      },
                    },
                  }}
                >
                  <MenuItem value="chat">Chat</MenuItem>
                  <MenuItem value="form">Form</MenuItem>
                  <MenuItem value="api_callback">API Callback</MenuItem>
                </Select>
              </FormControl>

              {formData.streamType === "api_callback" && (
                <FormControl>
                  <TextField
                    name="apiCallbackUrl"
                    label="API Callback URL"
                    value={formData.apiCallbackUrl}
                    onChange={onChange}
                  />
                </FormControl>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </KitModal>
  );
};

export default SettingsModal;
